import React from 'react'

import { Dashboard } from '@components/common/Dashboard/Dashboard'
import { PageLayout } from '@components/common/PageLayout'
import { DashboardBackButton } from '@components/utils'
import { SECTION_NAMES } from '@constants/sectionNames'
import {
  Banner,
  ButtonClean,
  HeadContent,
  Loader,
  Skeleton,
  Subsection,
  Tag,
} from '@fourthwall/components'
import { routing } from '@utils/routing'

import { ConnectBar } from '../../components/ConnectBar/ConnectBar'
import { YouTubeVideoEntry } from '../../components/YouTubeVideoEntry/YouTubeVideoEntry'
import { mapStatusToTagProps } from '../../utils/mapStatusToTagProps'
import { ConfigurationForm } from './components/ConfigurationForm/ConfigurationForm'
import { ConfigurationList } from './components/ConfigurationList/ConfigurationList'
import { ErrorBanner } from './components/ErrorBanner/ErrorBanner'
import { PrerequisitesErrors } from './components/PrerequisitesErrors/PrerequisitesErrors'
import { SettingsForm } from './components/SettingsForm/SettingsForm'
import { useTiktokShop } from './hooks/useTiktokShop'

export const AppsTiktokShop = () => {
  const { tiktokShop } = useTiktokShop()

  const hasSynchronizationErrors = Object.keys(tiktokShop.synchronizationErrors).some(
    (errorKey) =>
      tiktokShop.synchronizationErrors[errorKey as keyof typeof tiktokShop.synchronizationErrors],
  )

  const renderStatusTag = () => {
    const props = mapStatusToTagProps(tiktokShop.status)

    if (!props) return null

    return (
      <div style={{ marginBottom: 20 }}>
        <Tag {...props} size="large" display="inline-flex" />
      </div>
    )
  }

  const renderSynchronizationErrors = () => {
    const helpDocLink = (
      <a href={routing.external.helpCenter.tiktokShop} target="_blank" rel="noreferrer">
        help doc
      </a>
    )

    return (
      <>
        <ErrorBanner enabled={tiktokShop.synchronizationErrors.sellerNotActive}>
          Your Tiktok seller account is inactive. Please follow our {helpDocLink} guide.
        </ErrorBanner>

        <ErrorBanner enabled={tiktokShop.synchronizationErrors.shopNotActive}>
          Your Tiktok shop is inactive. Please follow our {helpDocLink} guide.
        </ErrorBanner>

        <ErrorBanner enabled={tiktokShop.synchronizationErrors.warehouseNotConfigured}>
          Your TikTok{' '}
          <a href={routing.external.tiktok.warehouse} target="_blank" rel="noreferrer">
            TikTok Shop warehouse
          </a>{' '}
          is either missing or does not contain the word "Fourthwall" in it's title. Please follow
          our {helpDocLink} guide.
        </ErrorBanner>

        <ErrorBanner enabled={tiktokShop.synchronizationErrors.returnWarehouseNotConfigured}>
          Your{' '}
          <a href={routing.external.tiktok.warehouse} target="_blank" rel="noreferrer">
            TikTok Shop return warehouse
          </a>{' '}
          is either missing or invalid. Please follow our {helpDocLink} guide.
        </ErrorBanner>

        <ErrorBanner enabled={tiktokShop.synchronizationErrors.shippingTemplate}>
          TikTok requires you to manually set up{' '}
          <a href={routing.external.tiktok.shippingTemplate} target="_blank" rel="noreferrer">
            TikTok Shop shipping rates
          </a>{' '}
          before we can sync your products. We recommend offering free shipping. Follow our{' '}
          {helpDocLink}
          to learn more.
        </ErrorBanner>

        <ErrorBanner enabled={tiktokShop.synchronizationErrors.incorrectShippingOption}>
          It looks like your shipping configuration is incorrect. To fix it:
          <ol>
            <li>Log into your TikTok Seller account</li>
            <li>Go to: Orders &rarr; Shipping Settings &rarr; Shipping and Delivery</li>
            <li>Click "Switch option" to choose "Seller shipping"</li>
            <li>Refresh this page and enable sales using the toggle below</li>
          </ol>
        </ErrorBanner>
      </>
    )
  }

  const renderNotConfiguredContent = () => {
    if (!tiktokShop.configurationStatus) {
      return (
        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'center' }}>
          <Loader size="large" appearance="secondary" />
        </div>
      )
    }

    if (tiktokShop.configurationStatus === 'SETUP_REQUIRED') {
      return (
        <ConfigurationList>
          <ConfigurationList.Item title="Create and connect TikTok Shop" />
          <ConfigurationList.Item title="Set up warehouse" />
          <ConfigurationList.Item title="Create shipping template" />
        </ConfigurationList>
      )
    }

    if (tiktokShop.configurationStatus === 'CONFIGURATION_REQUIRED') {
      return (
        <>
          {renderSynchronizationErrors()}
          <ErrorBanner enabled={tiktokShop.configurationPrerequisitesErrors.length > 0}>
            <PrerequisitesErrors errors={tiktokShop.configurationPrerequisitesErrors} />
          </ErrorBanner>

          <ConfigurationForm
            initialValues={tiktokShop.configurationForm.initialValues}
            shopOptions={tiktokShop.configurationForm.shops}
            warehouseOptions={tiktokShop.configurationForm.warehouses}
            isLoading={tiktokShop.configurationForm.isLoading}
            onShopSelect={tiktokShop.configurationForm.onShopSelect}
            onSubmit={tiktokShop.configurationForm.onSubmit}
          />
        </>
      )
    }
  }

  const renderConnectedContent = () => {
    return (
      <SettingsForm
        errorBanners={
          <>
            <ErrorBanner enabled={tiktokShop.paymentError && !tiktokShop.hasActivePaymentMethod}>
              TikTok Shop app requires active payment method to cover costs of orders (such as
              manufacturing or shipping we’re handling).{' '}
              <button onClick={tiktokShop.onPaymentMethodUpdate}>Set it up now</button>.
            </ErrorBanner>

            <ErrorBanner enabled={tiktokShop.paymentError && tiktokShop.hasActivePaymentMethod}>
              We were unable to charge your payment method to cover costs of your TikTok orders. In
              order to enable TikTok Shop sales please{' '}
              <button onClick={tiktokShop.onPaymentMethodUpdate}>update your payment method</button>
              .
            </ErrorBanner>

            {renderSynchronizationErrors()}

            <ErrorBanner enabled={tiktokShop.configurationPrerequisitesErrors.length > 0}>
              <PrerequisitesErrors errors={tiktokShop.configurationPrerequisitesErrors} />
            </ErrorBanner>

            <ErrorBanner
              enabled={
                hasSynchronizationErrors && !tiktokShop.settingsForm.initialValues.salesEnabled
              }
              appearance="warning"
            >
              Once you finalize the set up in TikTok Shop Seller Center please enable "TikTok Shop
              sales" below to synchronize your products once again.
            </ErrorBanner>
          </>
        }
        {...tiktokShop.settingsForm}
      />
    )
  }

  const renderContent = () => {
    if (tiktokShop.isLoading) {
      return null
    }

    if (tiktokShop.status === 'NOT_CONNECTED') {
      return null
    }

    if (tiktokShop.status === 'NOT_CONFIGURED') {
      return (
        <Subsection title="Configure your TikTok Shop">{renderNotConfiguredContent()}</Subsection>
      )
    }

    if (tiktokShop.status === 'CONNECTED') {
      return renderConnectedContent()
    }
  }

  return (
    <>
      <HeadContent title={SECTION_NAMES.appTikTokShop} />
      <PageLayout size="tiny">
        <Dashboard.Section
          name={SECTION_NAMES.appTikTokShop}
          topContent={<DashboardBackButton label="Apps" to={routing.apps.self} />}
        >
          <Subsection>
            {renderStatusTag()}
            <div>
              Showcase and sell products directly to your fans on TikTok.{' '}
              <ButtonClean
                label="Learn more"
                href={routing.external.helpCenter.tiktokShop}
                target="_blank"
              />
            </div>
          </Subsection>
          {tiktokShop.status === 'CONNECTED' && (
            <div style={{ marginTop: 20 }}>
              <Banner appearance="warning">
                You get paid directly on TikTok. Fourthwall will charge your account for any costs
                related to the order.{' '}
                <a
                  href={routing.external.helpCenter.tiktokShopPayment}
                  target="_blank"
                  rel="noreferrer"
                >
                  Learn more
                </a>
              </Banner>
            </div>
          )}
          <div style={{ marginTop: 20, marginBottom: 40 }}>
            {tiktokShop.connectBar ? (
              <ConnectBar {...tiktokShop.connectBar} />
            ) : (
              <Skeleton>
                <Skeleton.Shape height={64} />
              </Skeleton>
            )}
            {tiktokShop.status && (
              <div style={{ marginBlock: 20 }}>
                <YouTubeVideoEntry
                  title="Learn how to set up TikTok Shop"
                  description="Follow this easy video guide to see how to register for TikTok Shop and sell your products directly on TikTok Shop."
                  size={tiktokShop.status === 'NOT_CONNECTED' ? 'regular' : 'compact'}
                  videoId="zWiBZ1z6aDI"
                />
              </div>
            )}
          </div>
          {renderContent()}
        </Dashboard.Section>
      </PageLayout>
    </>
  )
}
